import { Badge, Button, EmptyState, Icon } from '@/components/ui';

import { formatDate, relativeTime } from '@/utils/format';

import { batchLabel, batchTone } from './stages';

/**
 * Every handover this company has made.
 *
 * Two columns matter more than they look. "Who asked" distinguishes a person
 * pressing a button from the scheduled run, which is the first question
 * anybody has when candidates were sent that nobody remembers sending. And a
 * partly-sent handover is labelled as such rather than rounded to success or
 * failure, because the candidates who did not go are coming back round.
 */
export function HandoverHistory({ batches, onDownload, downloading }) {
  if (!batches.length) {
    return <EmptyState title="No handovers yet" description="Nothing has been sent." />;
  }

  return (
    <div className="overflow-x-auto">
      <table className="w-full text-sm">
        <thead>
          <tr className="text-ink-muted border-line border-b text-left text-xs uppercase">
            <th className="py-2 pr-4 font-medium">When</th>
            <th className="py-2 pr-4 font-medium">Candidates</th>
            <th className="py-2 pr-4 font-medium">Who asked</th>
            <th className="py-2 pr-4 font-medium">Result</th>
            <th className="py-2 font-medium" />
          </tr>
        </thead>
        <tbody className="divide-line divide-y">
          {batches.map((batch) => (
            <tr key={batch.id}>
              <td className="text-ink py-3 pr-4 whitespace-nowrap">
                {formatDate(batch.created_at)}
                <span className="text-ink-muted ml-2 text-xs">
                  {relativeTime(batch.created_at)}
                </span>
              </td>
              <td className="text-ink py-3 pr-4">
                {batch.delivered_count} of {batch.candidate_count}
              </td>
              <td className="text-ink-muted py-3 pr-4">
                {batch.is_automatic ? 'Automatically' : (batch.triggered_by ?? 'Someone here')}
              </td>
              <td className="py-3 pr-4">
                <Badge tone={batchTone(batch.status)}>{batchLabel(batch.status)}</Badge>
              </td>
              <td className="py-3 text-right">
                {batch.has_document && (
                  <Button
                    variant="ghost"
                    size="xs"
                    title={`Download the handover of ${formatDate(batch.created_at)}`}
                    isLoading={downloading === batch.id}
                    onClick={() => onDownload(batch)}
                  >
                    <Icon name="download" size={14} />
                    Download
                  </Button>
                )}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
